import { TimeView } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { TeachClassSubjects } from '@protos/user_management/ums.login.apis_pb';
import {
  ModeOfConductionEnum,
  MultiSubjectSection,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { BloomLevelEnum } from '@protos/content_management/content.db_pb';

// ============================================================

const buildDateFromValues = (dateValue: string, timeValue: string) => {
  if (!dateValue || !timeValue) return null;
  // Take only the date portion (ignore the UTC shift)
  const [year, month, day] = dateValue.split('T')[0].split('-').map(Number);
  const [hours, minutes] = timeValue
    .split(':')
    .map((v) => parseInt(v.trim(), 10));

  return new Date(year, month - 1, day, hours, minutes); // local date/time
};

// ------------------------------------------------------------

interface IDisableStartTime {
  value: Date;
  view: TimeView;
  startTimeValues: { timeValue: string; dateValue: string };
}
export const disableStartTime = (props: IDisableStartTime) => {
  // values of time that are behind current time will be disabled
  const { value, view, startTimeValues } = props;
  const now = new Date();
  const selectedDate = buildDateFromValues(
    startTimeValues.dateValue,
    startTimeValues.timeValue
  );
  const isToday =
    selectedDate && selectedDate.toDateString() === now.toDateString();

  if (!isToday) return false;
  if (view === 'hours') {
    return value.getHours() < now.getHours();
  }
  if (view === 'minutes') {
    const selectedHour = parseInt(startTimeValues.timeValue.split(':')[0], 10);

    return (
      selectedHour === now.getHours() && value.getMinutes() < now.getMinutes()
    );
  }
  if (view === 'seconds') {
    return false; // no restriction on seconds
  }
  return false;
};

// ------------------------------------------------------------

interface IDisableEndTime extends IDisableStartTime {
  endTimeValues: { timeValue: string; dateValue: string };
}
export const disableEndTime = (props: IDisableEndTime): boolean => {
  // values of time that are behind current time and start time will be disabled
  const { value, view, startTimeValues, endTimeValues } = props;
  const now = new Date();
  const selectedEndDate = buildDateFromValues(
    endTimeValues.dateValue,
    endTimeValues.timeValue
  );
  const selectedStartDate = buildDateFromValues(
    startTimeValues.dateValue,
    startTimeValues.timeValue
  );

  // If no end date, allow all times
  if (!selectedEndDate) return false;

  const isToday = selectedEndDate.toDateString() === now.toDateString();
  // If not today → only enforce after start time if same date as start
  if (!isToday) {
    if (
      selectedStartDate &&
      selectedEndDate.toDateString() === selectedStartDate.toDateString()
    ) {
      if (view === 'hours') {
        return value.getHours() < selectedStartDate.getHours();
      }
      if (view === 'minutes') {
        return (
          value.getHours() === selectedStartDate.getHours() &&
          value.getMinutes() < selectedStartDate.getMinutes()
        );
      }
    }
    return false;
  }

  // If today → block past times + times before start
  if (view === 'hours') {
    return (
      value.getHours() < now.getHours() ||
      (!!selectedStartDate && value.getHours() < selectedStartDate.getHours())
    );
  }

  if (view === 'minutes') {
    const sameHourAsNow = value.getHours() === now.getHours();
    const sameHourAsStart =
      !!selectedStartDate && value.getHours() === selectedStartDate.getHours();

    return (
      (sameHourAsNow && value.getMinutes() < now.getMinutes()) ||
      (sameHourAsStart && value.getMinutes() < selectedStartDate.getMinutes())
    );
  }

  if (view === 'seconds') {
    return false;
  }

  return false;
};

// ============================================================

function formatTime(timeValue: string) {
  // Remove spaces and ensure two digits for hours and minutes
  const [hoursRaw, minutesRaw] = timeValue.split(':');
  const hours = hoursRaw.trim().padStart(2, '0');
  const minutes = (minutesRaw || '0').trim().padStart(2, '0');
  return `${hours}:${minutes}`;
}

// ------------------------------------------------------------

const isEarlier = (a: Date, b: Date) =>
  a.getHours() < b.getHours() ||
  (a.getHours() === b.getHours() && a.getMinutes() < b.getMinutes());

// ------------------------------------------------------------

const isEarlierOrSame = (a: Date, b: Date) =>
  a.getHours() < b.getHours() ||
  (a.getHours() === b.getHours() && a.getMinutes() <= b.getMinutes());

// ------------------------------------------------------------

interface IWrongStartTime {
  startTimeValues: { timeValue: string; dateValue: string };
}
export const wrongStartTime = ({ startTimeValues }: IWrongStartTime) => {
  const now = new Date();
  now.setSeconds(0, 0);
  const selectedDate = new Date(
    `${startTimeValues.dateValue.split('T')[0]}T${formatTime(
      startTimeValues.timeValue
    )}`
  );
  selectedDate.setSeconds(0, 0);

  // day-only comparison for date rule
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sd = new Date(`${startTimeValues.dateValue.split('T')[0]}`); // incase .timeValue gives NaN
  const onlyStartDate = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
  const wrongDate = onlyStartDate < today;

  // only compare time-of-day if it's the same calendar day
  const isSameDay = selectedDate.toDateString() === now.toDateString();
  const wrongTime = isSameDay && isEarlier(selectedDate, now);
  const [hours, minutes] = startTimeValues.timeValue
    .split(':')
    .map((v) => parseInt(v.trim(), 10));
  const AmOrPmNotPicked = isNaN(hours) && isNaN(minutes);

  return {
    wrongDate: wrongDate || !startTimeValues.dateValue,
    wrongTime: wrongTime || AmOrPmNotPicked || !startTimeValues.dateValue,
  };
};

// ============================================================

interface IWrongEndTime {
  startTimeValues: { timeValue: string; dateValue: string };
  endTimeValues: { timeValue: string; dateValue: string };
}
export const wrongEndTime = ({
  startTimeValues,
  endTimeValues,
}: IWrongEndTime) => {
  const now = new Date();
  now.setSeconds(0, 0);

  const startingPoint = new Date(
    `${startTimeValues.dateValue.split('T')[0]}T${formatTime(
      startTimeValues.timeValue
    )}`
  );
  startingPoint.setSeconds(0, 0);
  const endingPoint = new Date(
    `${endTimeValues.dateValue.split('T')[0]}T${formatTime(
      endTimeValues.timeValue
    )}`
  );
  endingPoint.setSeconds(0, 0);

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sd = new Date(`${startTimeValues.dateValue.split('T')[0]}`);
  const startDay = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
  const ed = new Date(`${endTimeValues.dateValue.split('T')[0]}`);
  const endDay = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
  const wrongDate = endDay < today || endDay < startDay;

  const sameDayAsNow = endingPoint.toDateString() === now.toDateString();
  const sameDayAsStart =
    endingPoint.toDateString() === startingPoint.toDateString();
  const wrongTime =
    (sameDayAsNow && isEarlierOrSame(endingPoint, now)) ||
    (sameDayAsStart && isEarlierOrSame(endingPoint, startingPoint));
  const [hours, minutes] = endTimeValues.timeValue
    .split(':')
    .map((v) => parseInt(v.trim(), 10));
  const AmOrPmNotPicked = isNaN(hours) && isNaN(minutes);

  return {
    wrongDate: wrongDate || !endTimeValues.dateValue,
    wrongTime: wrongTime || AmOrPmNotPicked || !endTimeValues.dateValue,
  };
};

// ============================================================

// to rerender if time has passed
export const useCurrentMinute = () => {
  const [minute, setMinute] = useState(new Date().getMinutes());
  useEffect(() => {
    const interval = setInterval(() => {
      const newMinute = new Date().getMinutes();
      setMinute((prevMinute) =>
        prevMinute !== newMinute ? newMinute : prevMinute
      );
    }, 1000); // check every second

    return () => clearInterval(interval);
  }, []);

  return minute;
};

// ============================================================

interface SubjectListItem {
  subjectName: string;
  id: number;
}

/**
 * Builds array of { subjectName, id } for an assessment card using
 * card.subjectIds, card.classId and subject names from teachClassSubjects.
 */
export function getSubjectListForAssessmentCard(
  card: { subjectIds?: number[]; classId?: number },
  teachClassSubjects?: TeachClassSubjects[]
): SubjectListItem[] {
  const subjectIds = card.subjectIds ?? [];
  const classId = card.classId;
  if (!subjectIds.length || classId == null || !teachClassSubjects?.length) {
    return [];
  }
  const classInfo = teachClassSubjects.find(
    (c) => (c as { classId?: number }).classId === classId
  );
  if (!classInfo?.subjects?.length) return [];
  const list: SubjectListItem[] = [];
  for (const subjectId of subjectIds) {
    const subject = classInfo.subjects.find(
      (s) => (s as { subjectId?: number }).subjectId === subjectId
    );
    const name =
      (subject as { subjectName?: string } | undefined)?.subjectName ?? '';
    list.push({ subjectName: name, id: subjectId });
  }
  return list;
}

// ============================================================

export const MAXIMUM_MARKS_LIMIT = 20;
export const OMR_MAX_QUESTIONS = 60;
const OMR_MAX_SET_COUNT = 4;
const DEFAULT_MAX_QUESTIONS = 200;

export const getSectionQuestionCount = (
  section?: MultiSubjectSection
): number =>
  section?.multiSubjectSubSections?.reduce(
    (acc, subSection) => acc + (subSection.subSectionMetaData?.length ?? 0),
    0
  ) ?? 0;

// ------------------------------------------------------------

export const getTotalAssessmentQuestionCount = (
  multiSubjectSections?: MultiSubjectSection[]
): number =>
  multiSubjectSections?.reduce(
    (total, section) => total + getSectionQuestionCount(section),
    0
  ) ?? 0;

// ------------------------------------------------------------

export const getQuestionCountInOtherSections = (params: {
  multiSubjectSections?: MultiSubjectSection[];
  currentSectionNo?: number;
}): number => {
  const { multiSubjectSections, currentSectionNo } = params;
  const total = getTotalAssessmentQuestionCount(multiSubjectSections);
  const currentSection = multiSubjectSections?.find(
    (section) => section.sectionNo === currentSectionNo
  );
  return total - getSectionQuestionCount(currentSection);
};

// ------------------------------------------------------------

/** Question count in all sections before `currentSectionNo` (for continuous numbering). */
export const getQuestionNumberOffsetForSection = (params: {
  multiSubjectSections?: MultiSubjectSection[];
  currentSectionNo?: number;
}): number => {
  const { multiSubjectSections, currentSectionNo } = params;
  if (!multiSubjectSections?.length || currentSectionNo == null) return 0;

  const sortedSections = [...multiSubjectSections].sort(
    (a, b) => (a.sectionNo ?? 0) - (b.sectionNo ?? 0)
  );

  let offset = 0;
  for (const section of sortedSections) {
    if (section.sectionNo === currentSectionNo) break;
    offset += getSectionQuestionCount(section);
  }
  return offset;
};

// ------------------------------------------------------------

export const getOmrRemainingQuestionSlotsForSection = (params: {
  multiSubjectSections?: MultiSubjectSection[];
  currentSectionNo?: number;
}): number =>
  Math.max(0, OMR_MAX_QUESTIONS - getQuestionCountInOtherSections(params));

// ------------------------------------------------------------

export const getMaxNoOfQuestionsLimit = (params: {
  totalAvailableFromTypes: number;
  modeOfConduction?: ModeOfConductionEnum;
  globalMax?: number;
  questionsInOtherSections?: number;
}): number => {
  const {
    totalAvailableFromTypes,
    modeOfConduction,
    globalMax = DEFAULT_MAX_QUESTIONS,
    questionsInOtherSections,
  } = params;
  if (totalAvailableFromTypes === 0) return 0;
  let limit = Math.min(globalMax, totalAvailableFromTypes);
  if (modeOfConduction === ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_OMR) {
    const remainingForSection =
      OMR_MAX_QUESTIONS - (questionsInOtherSections ?? 0);
    limit = Math.min(limit, Math.max(0, remainingForSection));
  }
  return limit;
};

// ------------------------------------------------------------

export const OMR_MAX_QUESTIONS_LIMIT_MESSAGE =
  'Only 60 questions can be added to the OMR assessment';

export const isOmrQuestionLimitReached = (
  modeOfConduction: ModeOfConductionEnum | undefined,
  currentQuestionCount: number
): boolean =>
  modeOfConduction === ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_OMR &&
  currentQuestionCount >= OMR_MAX_QUESTIONS;

// ============================================================

function factorial(n: number): number {
  let result = 1;

  for (let i = 2; i <= n; i++) {
    result *= i;
  }

  return result;
}

// ------------------------------------------------------------

export const getMaxSets = (questionCount: number): number => {
  return Math.min(factorial(questionCount), OMR_MAX_SET_COUNT);
};

// ============================================================

export const resolveModeOfConduction = (params: {
  creationType?: string;
  manageMode?: ModeOfConductionEnum;
  assessmentMode?: ModeOfConductionEnum;
}) => {
  const { creationType, manageMode, assessmentMode } = params;

  return creationType === 'Readymade' ? manageMode : assessmentMode;
};

// ============================================================

export interface ConductionCapabilities {
  configs: {
    reattempt: boolean;
    shuffling: boolean;
    timed: boolean;
    proctoring: boolean;
    questionBasedTimer: boolean;
  };
  features: {
    chooseSection: boolean;
    noOfSets: boolean;
    attendance: boolean;
  };
  meta: {
    isClicker: boolean;
    isOMR: boolean;
    isClickerOrOMR: boolean;
  };
}

// ============================================================

export const getConductionCapabilities = (
  mode?: ModeOfConductionEnum
): ConductionCapabilities => {
  switch (mode) {
    case ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER:
      return {
        configs: {
          reattempt: false,
          shuffling: false,
          timed: false,
          proctoring: false,
          questionBasedTimer: true,
        },
        features: {
          chooseSection: true,
          noOfSets: false,
          attendance: false,
        },
        meta: {
          isClicker: true,
          isOMR: false,
          isClickerOrOMR: true,
        },
      };

    case ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_OMR:
      return {
        configs: {
          reattempt: false,
          shuffling: false,
          timed: false,
          proctoring: false,
          questionBasedTimer: false,
        },
        features: {
          chooseSection: false,
          noOfSets: true,
          attendance: false,
        },
        meta: {
          isClicker: false,
          isOMR: true,
          isClickerOrOMR: true,
        },
      };

    default:
      return {
        configs: {
          reattempt: true,
          shuffling: true,
          timed: true,
          proctoring: true,
          questionBasedTimer: false,
        },
        features: {
          chooseSection: true,
          noOfSets: false,
          attendance: true,
        },
        meta: {
          isClicker: false,
          isOMR: false,
          isClickerOrOMR: false,
        },
      };
  }
};

// ============================================================

/** Question count in all sections before `currentSectionNo` using `taskQuestionInfo` (for continuous numbering). */
export const getQuestionNumberOffsetFromTaskQuestionInfo = (params: {
  taskQuestionInfo?: { sectionNo?: number; questionId?: string }[];
  currentSectionNo?: number;
}): number => {
  const { taskQuestionInfo, currentSectionNo } = params;
  if (!taskQuestionInfo?.length || currentSectionNo == null) return 0;

  return taskQuestionInfo.filter(
    (info) =>
      info.questionId &&
      info.sectionNo != null &&
      info.sectionNo < currentSectionNo
  ).length;
};

// ============================================================

type AssessmentContentQuestion = NonNullable<
  AssessmentContent['assessmentContent'][number]
>;

/**
 * Returns assessment questions for display, filtered and ordered by the selected
 * section. For multi-subject formative assessments, uses
 * `questionsSequenceInfo.taskQuestionInfo` (matched by `sectionNo`, sorted by
 * `sequence`) and maps each `questionId` to `assessmentContent`.
 */
export const getDisplayedAssessmentQuestions = (params: {
  assessmentContent?: AssessmentContent;
  selectedSectionNo?: number;
}): AssessmentContentQuestion[] => {
  const { assessmentContent, selectedSectionNo } = params;
  if (!assessmentContent) return [];

  const isMultiSubjectFormative =
    assessmentContent.assessment?.taskType ===
    TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM;

  const taskQuestionInfo =
    assessmentContent.questionsSequenceInfo?.taskQuestionInfo ?? [];

  const allQuestionIds = assessmentContent.assessmentContent
    .map((item) => item.questionId)
    .filter((id): id is string => id !== undefined);

  const sortedQuestionIds =
    isMultiSubjectFormative && selectedSectionNo != null
      ? taskQuestionInfo
          .filter((info) => info.sectionNo === selectedSectionNo)
          .sort((a, b) => a.sequence - b.sequence)
          .map((info) => info.questionId)
          .filter((id): id is string => id !== undefined)
      : allQuestionIds;

  return sortedQuestionIds
    .map((questionId) =>
      assessmentContent.assessmentContent.find(
        (item) => item.questionId === questionId
      )
    )
    .filter((item): item is AssessmentContentQuestion => item !== undefined);
};

// ============================================================

const DIFFICULTY_TIME_PER_QUESTION: Record<string, [number, number, number]> = {
  Easy: [0.5, 0.75, 1],
  Medium: [1, 1.75, 2.5],
  Hard: [2.5, 3.25, 4],
};

/** Middle (default) approximate-time estimate in minutes: questionCount * mid-tier minutes-per-question for the difficulty. */
export const getMiddleApproximateTime = (params: {
  difficulty?: string;
  questionCount?: number;
}): number => {
  const { difficulty, questionCount } = params;
  const times =
    (difficulty && DIFFICULTY_TIME_PER_QUESTION[difficulty]) ||
    DIFFICULTY_TIME_PER_QUESTION.Medium;
  return Math.round((questionCount ?? 0) * times[1]);
};

// ============================================================

export const BLOOM_LEVEL_ARRAY = [
  { title: 'Retention', typeKey: BloomLevelEnum.BLOOMLEVEL_RETENTION },
  { title: 'Analysis', typeKey: BloomLevelEnum.BLOOMLEVEL_ANALYSIS },
  { title: 'Application', typeKey: BloomLevelEnum.BLOOMLEVEL_APPLICATION },
  { title: 'Comprehension', typeKey: BloomLevelEnum.BLOOMLEVEL_COMPREHENSION },
  { title: 'Creation', typeKey: BloomLevelEnum.BLOOMLEVEL_CREATION },
  { title: 'Evaluation', typeKey: BloomLevelEnum.BLOOMLEVEL_EVALUATION },
];

// ============================================================
